import { useMemo, useState } from 'react'
import { createAvatar } from '@dicebear/core'
import { avataaars, adventurer, bottts, funEmoji, lorelei } from '@dicebear/collection'
import { createLogger } from '../../utils/logger'

const logger = createLogger('Avatar')

// Constants
const DICEBEAR_SIZE = 128

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500'
]

// Size classes - defined outside component to avoid recreation
const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-12 h-12 text-lg',
  lg: 'w-20 h-20 text-2xl',
  xl: 'w-32 h-32 text-4xl'
}

// Available avatar styles
export const AVATAR_STYLES = {
  avataaars: { name: 'Cartoon', collection: avataaars },
  adventurer: { name: 'Adventurer', collection: adventurer },
  bottts: { name: 'Robot', collection: bottts },
  funEmoji: { name: 'Emoji', collection: funEmoji },
  lorelei: { name: 'Lorelei', collection: lorelei }
}

// Hash function for consistent colors
function hashCode(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return hash
}

function getColorFromHash(str) {
  const hash = hashCode(str || 'default')
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const Avatar = ({
  type = 'dicebear',
  seed,
  style = 'bottts',
  data,
  name,
  publicName,
  initials: providedInitials,
  size = 'md',
  className = ''
}) => {
  // Track image load errors to fallback to initials
  const [imageError, setImageError] = useState(false)

  const initials = useMemo(() => {
    if (providedInitials) return providedInitials
    if (name) {
      const parts = name.split(' ')
      return parts.length >= 2
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : name.slice(0, 2).toUpperCase()
    }
    return publicName?.slice(0, 2).toUpperCase() || '?'
  }, [name, publicName, providedInitials])

  const bgColor = useMemo(() => {
    return getColorFromHash(publicName || name || 'default')
  }, [publicName, name])

  // Generate DiceBear avatar
  const dicebearSvg = useMemo(() => {
    if (type !== 'dicebear') return null

    // Use seed if provided, otherwise fall back to publicName or name
    const effectiveSeed = seed || publicName || name || 'default'
    const avatarStyle = AVATAR_STYLES[style] || AVATAR_STYLES.bottts
    try {
      const avatar = createAvatar(avatarStyle.collection, {
        seed: effectiveSeed,
        size: DICEBEAR_SIZE,
        translateY: 10 // Shift character down to center vertically in viewBox
      })
      return avatar.toDataUri()
    } catch (error) {
      logger.error('DiceBear avatar generation failed:', error)
      return null // Falls back to initials
    }
  }, [type, seed, style, publicName, name])

  // Handle image load error - fallback to initials
  const handleImageError = () => {
    logger.error('Avatar image failed to load')
    setImageError(true)
  }

  // Initials fallback component
  const InitialsFallback = () => (
    <div
      className={`
        rounded-full flex items-center justify-center font-bold text-white
        ${bgColor} ${SIZE_CLASSES[size]} ${className}
      `}
    >
      {initials}
    </div>
  )

  // Render based on type (with error fallback)
  if (type === 'custom' && data && !imageError) {
    return (
      <div
        className={`rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0 ${SIZE_CLASSES[size]} ${className}`}
      >
        <img
          src={data}
          alt="Profile avatar"
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      </div>
    )
  }

  if (type === 'dicebear' && dicebearSvg && !imageError) {
    return (
      <div
        className={`rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0 ${SIZE_CLASSES[size]} ${className}`}
      >
        <img
          src={dicebearSvg}
          alt="Profile avatar"
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      </div>
    )
  }

  // Default: initials (also used as fallback for errors)
  return <InitialsFallback />
}

export default Avatar
